import { createAction, props } from '@ngrx/store';
import { CandidateInfo } from 'src/app/esign/models/candidate-info.model';
import { CandidateData } from 'src/app/esign/models/envelope-send.model';
import { RequiredDocument } from 'src/app/esign/models/required-document.model';
import { ExistingEnvelope } from 'src/app/esign/models/existing-envelope.model';
import { TemplateFolder } from 'src/app/esign/models/template-folder.model';
import { Template } from 'src/app/esign/models/template.model';
import { PortalEnvelope } from 'src/app/esign/models/portal-envelope.model';
import { EsignPermissions } from 'src/app/esign/models/esign-permissions.model';

export const getEsignPermissions = createAction('[Esign Component] Get Esign Permissions');

export const getEsignPermissionsSuccess = createAction(
    '[Esign API] Get Esign Permissions Success',
    props<{ esignPermissions: EsignPermissions }>()
);

export const getEsignPermissionsFailure = createAction(
    '[Esign API] Get Esign Permissions Failure',
    props<{ error: unknown }>()
);

export const getTemplateImage = createAction(
    '[Esign Component] Get Document Image',
    props<{ externalTemplateId: string; templateName: string }>()
);

export const getTemplateImageSuccess = createAction(
    '[Esign API] Get Document Image Success',
    props<{ templateName: string; image: Uint8Array }>()
);

export const getTemplateImageFailure = createAction(
    '[Esign API] Get Document Image Failure',
    props<{ error: unknown }>()
);

export const getCandidateInfo = createAction('[Esign Component] Get Candidate Info', props<{ contractId: number }>());

export const getCandidateInfoSuccess = createAction(
    '[Esign API] Get Candidate Info Success',
    props<{ candidateInfo: CandidateInfo }>()
);

export const getCandidateInfoFailure = createAction(
    '[Esign API] Get Candidate Info Failure',
    props<{ error: unknown }>()
);

export const getRequiredDocuments = createAction(
    '[Esign Component] Get Required Documents',
    props<{ contractId: number }>()
);

export const getRequiredDocumentsSuccess = createAction(
    '[Esign API] Get Required Documents Success',
    props<{ requiredDocuments: RequiredDocument[] }>()
);

export const getRequiredDocumentsFailure = createAction(
    '[Esign API] Get Required Documents Failure',
    props<{ error: unknown }>()
);

export const removeRequiredDocument = createAction(
    '[Esign Component] Remove Required Document From Envelope',
    props<{ documentTypeId: number }>()
);

export const getTemplateFolders = createAction(
    '[Template Folder List Component] Get Template Folders',
    props<{ forceRefresh: boolean }>()
);

export const getTemplateFoldersSuccess = createAction(
    '[Esign API] Get Template Folders Success',
    props<{ folders: TemplateFolder[] }>()
);

export const getTemplateFoldersFailure = createAction(
    '[Esign API] Get Template Folders Failure',
    props<{ error: unknown }>()
);

export const setSelectedFolder = createAction(
    '[Template Folder List Component] Set Selected Folder',
    props<{ folderId: string }>()
);

export const clearSelectedFolder = createAction('[Template Mapping Fly Out] Clear Selected Folder');

export const searchTemplates = createAction(
    '[Template Mapping Fly Out Component] Search Templates',
    props<{ folderId: string; name: string }>()
);

export const searchTemplatesSuccess = createAction(
    '[Esign API] Search Templates Success',
    props<{ templates: Template[] }>()
);

export const searchTemplatesFailure = createAction('[Esign API] Search Templates Failure', props<{ error: unknown }>());

export const setSelectedDocumentType = createAction(
    '[Document Type Component] Set Selected Document Type',
    props<{ requirement: RequiredDocument }>()
);

export const clearTemplateMappingState = createAction(
    '[Template Mapping Fly Out Component] Clear Template Mapping State'
);

export const associateTemplateDocumentType = createAction(
    '[Template Mapping Fly Out Component] Associate Template To Document Type',
    props<{ template: Template; documentTypeId: number; contractId: number }>()
);

export const associateTemplateDocumentTypeSuccess = createAction(
    '[Esign API] Associate Template To Document Type Success',
    props<{ template: Template; documentTypeId: number; templateId: number; stateId: number }>()
);

export const associateTemplateDocumentTypeFailure = createAction(
    '[Esign API] Associate Template To Document Type Failure',
    props<{ error: unknown; contractId: number }>()
);

export const associateTemplateDocumentTypeAdHoc = createAction(
    '[Template Mapping Fly Out Component] Associate Template to Document Type Ad Hoc',
    props<{ template: Template; documentTypeId: number; contractId: number }>()
);

export const associateTemplateDocumentTypeAdHocSuccess = createAction(
    '[Template Mapping Fly Out Component] Associate Template to Document Type Ad Hoc Success',
    props<{ template: Template; documentTypeId: number; templateId: number; stateId: number }>()
);

export const associateTemplateDocumentTypeAdHocFailure = createAction(
    '[Template Mapping Fly Out Component] Associate Template to Document Type Ad Hoc Failure',
    props<{ error: unknown; contractId: number }>()
);

export const disassociateTemplateDocumentType = createAction(
    '[Document Tile Component] Disassociate Template And Document Type',
    props<{ internalTemplateId: number; documentTypeId: number; contractId: number; stateId: number }>()
);

export const disassociateTemplateDocumentTypeSuccess = createAction(
    '[Esign API] Disassociate Template And Document Type Success',
    props<{ documentTypeId: number }>()
);

export const disassociateTemplateDocumentTypeFailure = createAction(
    '[Esign API] Disassociate Template And Document Type Failure',
    props<{ error: unknown; contractId: number }>()
);

export const sendEnvelope = createAction(
    '[Envelope Send Dialog Component] Send Envelope',
    props<{ recipient: CandidateData }>()
);

export const sendEnvelopeSuccess = createAction('[Esign API] Send Envelope Type Success');

export const sendEnvelopeFailure = createAction('[Esign API] Send Envelope Type Failure', props<{ error: unknown }>());

export const getEnvelope = createAction('[Send Envelope Esign Effect] Get Envelope', props<{ envelopeId: number }>());

export const getEnvelopeSuccess = createAction(
    '[Esign API] Get Envelope Success',
    props<{ envelope: ExistingEnvelope }>()
);

export const getEnvelopeFailure = createAction('[Esign API] Get Envelope Failure', props<{ error: unknown }>());

export const getContractEnvelopes = createAction(
    '[Esign Component] Get Contract Envelopes',
    props<{ contractId: number }>()
);

export const getContractEnvelopesSuccess = createAction(
    '[Esign API] Get Contract Envelopes Success',
    props<{ envelopes: ExistingEnvelope[] }>()
);

export const getContractEnvelopesFailure = createAction(
    '[Esign API] Get Contract Envelopes Failure',
    props<{ error: unknown }>()
);

export const getPortalEnvelopes = createAction(
    '[Esign Component] Get Portal Envelopes',
    props<{ candidateId: number }>()
);

export const getPortalEnvelopesSuccess = createAction(
    '[Esign API] Get Portal Envelopes Success',
    props<{ envelopes: PortalEnvelope[] }>()
);

export const getPortalEnvelopesFailure = createAction(
    '[Esign API] Get Portal Envelopes Failure',
    props<{ error: unknown }>()
);

export const getEsignDocuments = createAction(
    '[Add Document Fly Out Component] Get Esign Documents',
    props<{ contractId: number }>()
);

export const getEsignDocumentsSuccess = createAction(
    '[Esign API] Get Esign Documents Success',
    props<{ documents: RequiredDocument[] }>()
);

export const getEsignDocumentsFailure = createAction(
    '[Esign API] Get Esign Documents Failure',
    props<{ error: unknown }>()
);

export const addDocumentToEnvelope = createAction(
    '[Add Document Fly Out Component] Add Document To Envelope',
    props<{ document: RequiredDocument }>()
);

export const cancelEnvelope = createAction(
    '[Envelope Cancel Dialog Component] Cancel Envelope',
    props<{ envelopeId: number; contractId: number }>()
);

export const cancelEnvelopeSuccess = createAction(
    '[Esign API] Cancel Envelope Type Success',
    props<{ envelopeId: number; contractId: number }>()
);

export const cancelEnvelopeFailure = createAction(
    '[Esign API] Cancel Envelope Type Failure',
    props<{ error: unknown }>()
);

export const setCreateMode = createAction('[Esign Component] Set Create Mode', props<{ isCreateMode: boolean }>());

export const resendEnvelope = createAction(
    '[Existing Envelope Component] Resend Envelope',
    props<{ internalEnvelopeId: number }>()
);

export const resendEnvelopeSuccess = createAction('[Esign API] Resend Envelope Type Success');

export const resendEnvelopeFailure = createAction(
    '[Esign API] Resend Envelope Type Failure',
    props<{ error: unknown }>()
);

export const setTemplateSearchComplete = createAction(
    '[Esign Component] Set Template Search Completion',
    props<{ isTemplateSearchComplete: boolean }>()
);
